/**
 * useTenantInstanceQuotaByProvider — cota de instâncias por provider.
 *
 * ARQUITETURA:
 * - 100% server-side via RPC `get_tenant_instance_quota_by_provider`
 * - Retorna, por provider: contratado (allowed_qty), em uso (in_use_qty)
 *   e disponível (available_qty), já considerando plano + add-ons pagos.
 * - Mesma queryKey usada pelo `AddInstanceDialog` para compartilhar cache
 *   (evita chamada duplicada na aba Instâncias).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-19 v1.0.0 - Versão inicial (extraído do AddInstanceDialog
 *            para alimentar também os KPIs por canal/provider).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useTenantInstanceQuotaByProvider');

/** Linha de cota retornada pela RPC (uma por provider elegível). */
export interface ProviderQuotaRow {
  provider_id: string;
  provider_name: string;
  display_name: string | null;
  channel_type: string;
  /** Contratado (plano + add-ons). -1 = ilimitado. */
  allowed_qty: number;
  in_use_qty: number;
  available_qty: number;
}

/**
 * Busca a cota por provider do tenant.
 *
 * @param tenantId - tenant atual (enviado como p_tenant_hint; validado no backend)
 * @param enabled  - permite adiar a busca (ex.: modal fechado)
 */
export function useTenantInstanceQuotaByProvider(
  tenantId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['tenant-instance-quota', tenantId],
    enabled: enabled && !!tenantId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ProviderQuotaRow[]> => {
      const { data, error } = await supabase.rpc(
        'get_tenant_instance_quota_by_provider' as never,
        { p_tenant_hint: tenantId } as never,
      );

      if (error) {
        logger.error('Erro ao buscar cota por provider', { error });
        throw error;
      }

      return (data || []) as ProviderQuotaRow[];
    },
  });
}
